<template>
  <div class="login-email-form">
    <h3 class="mb-l">
      {{ $t("title.login") }}
    </h3>
    <bf-form
      :ref="formName"
      class="form-email mt-xs lastpass-area"
      :model="form"
      :rules="formRules"
      @submit.prevent.native="checkEnter()"
    >
      <bf-form-item prop="email" :label="$t('form.emailLabel')">
        <bf-input
          ref="input"
          :key="`email-${emailkey}`"
          v-model.trim="form.email"
          autocomplete="off"
          name="username"
          class="w-100"
          :placeholder="$t('form.enterYour', { name: $t('form.email') })"
          :clearable="true"
          :disabled="isCallingApi"
          @change="handleChange"
          @keydown.native="handleComplete"
          @keydown.enter.native="onSubmit"
        >
          <template slot="append">
            <bf-button
              :disabled="isCallingApi"
              :loading="isCallingApi"
              type="primary"
              @click.native.stop.prevent="onSubmit"
            >
              <bf-icon
                size="20"
                name="arrow-forward"
              />
            </bf-button>
          </template>
        </bf-input>

        <bf-button
          class="submit-email"
          :disabled="isCallingApi"
          :loading="isCallingApi"
          type="primary"
          @click.native.stop.prevent="onSubmit"
        >
          <bf-icon
            size="20"
            name="arrow-forward"
          />
        </bf-button>

      </bf-form-item>

      <bf-form-item
        class="opacity-0 pointer-events-none"
        prop="password"
      >
        <bf-input
          type="password"
          autocomplete="on"
          name="password"
          showPassword
          v-model="form.password"
          class="w-100"
          :placeholder="$t('form.enterYour', { name: $t('form.password') })"
        >
        </bf-input>
      </bf-form-item>

    </bf-form>
    <div>
      <google-sign-in />

      <bf-link>
        <router-link
          :to="this.callbackUrl
          ? `/forgot-password?${this.callbackPrefix}=${this.callbackUrl}`
          : '/forgot-password'"
        >{{
          $t("title.forgotPassword")
        }}</router-link>
      </bf-link>
      <div class="mt-m">
        {{ $t("common.noAccount") }}
        <bf-link
          ><router-link
            :to="this.callbackUrl
            ? `/register?${this.callbackPrefix}=${this.callbackUrl}`
            : '/register'"
          >{{
            $t("form.register")
          }}</router-link></bf-link
        >
      </div>
    </div>
    <footer-ads/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import GoogleSignIn from '@/components/forms/GoogleSignIn.vue';
import RULES from '@/config/rules';
import { reCaptcha, showReCaptcha, hideReCaptcha } from '@/utils/reCaptcha';
import loginService from '@/services/login';
import FooterAds from '@/components/commons/FooterAds.vue';
import { validateEmailLength } from '@/utils/common';

export default {
  name: 'LogIn',
  props: {
    email: {
      type: String,
      default: '',
    },
    password: {
      type: String,
      default: '',
    },
    count: {
      type: Number,
      default: 0,
    },
  },
  components: {
    GoogleSignIn,
    FooterAds,
  },
  data() {
    return {
      emailkey: 1,
      formName: 'loginIn',
      form: {
        email: '',
        password: '',
      },
      isCallingApi: false,
    };
  },
  watch: {
    count(nextValue, oldValue) {
      if (nextValue !== oldValue) {
        const inputElement = this.$refs.input?.$el?.querySelector('input');
        if (this?.form?.password !== this?.password) {
          this.form.password = this.password;
        }
        this.form.email = this.email;
        inputElement.select();
        inputElement.focus();
      }
    },
    'form.email': async function formEmail(nextValue, oldValue) {
      if (nextValue !== oldValue) {
        await new Promise((r) => setTimeout(r, 100));
        const inputElement = this.$refs.input?.$el?.querySelector('input');
        inputElement.focus();
      }
    },
  },
  mounted() {
    this.$refs?.input?.$el?.querySelector('input').focus();
    showReCaptcha();
  },
  destroyed() {
    hideReCaptcha();
  },
  computed: {
    ...mapGetters('device', [
      'isMobile',
    ]),
    ...mapGetters('auth', [
      'callbackUrl', 'callbackPrefix',
    ]),
    formRules() {
      return {
        email: [
          { required: true, message: this.$t('form.pleaseInput', { name: this.$t('form.email') }), trigger: ['change'] },
          { pattern: RULES.PATTERN_EMAIL, message: this.$t('form.validator.invalid', { field: 'Email' }), trigger: ['change'] },
          {
            validator: validateEmailLength,
            message: `${this.$t('form.validator.limit', { field: 'Email', min: 1, max: 191 })}. ${this.$t('form.validator.emailLength', { field: 'email', min: 1, max: 64 })}`,
            trigger: ['change'],
          },
        ],
      };
    },
  },
  methods: {
    async handleComplete(e) {
      const ele = e.target;
      // Wait Autocomplete in 10 ms
      await new Promise((r) => setTimeout(r, 10));
      const nextEmail = ele.value;
      if (this.form.email !== nextEmail) {
        this.form.email = nextEmail;
      }
      try {
        this.$refs[this.formName].validate();
      } catch (e) {
        // pass
      }
    },
    async handleChange(value) {
      if (value !== this.form.email) {
        await this.$nextTick();
        this.emailkey += 1;
      }
    },
    async onSubmit() {
      this.$refs[this.formName].validate(async (valid) => {
        if (valid) {
          try {
            const payload = {
              email: this.form.email,
              password: '',
              type: 'CHECK',
            };
            this.isCallingApi = true;
            const reCaptchaToken = await reCaptcha();
            await loginService.login(payload, reCaptchaToken);
            this.isCallingApi = false;
            this.$emit('updateEmail', this?.form?.email);
            if (this?.form?.password) {
              this.$emit('updatePassword', this.form.password);
            }
            this.$emit('goToStep', 'step-2');
          } catch (error) {
            const self = this;
            this.isCallingApi = false;
            if (error?.response?.data?.error === 'UserNotExistsException') {
              this.$refs?.input?.$el?.querySelector('input').focus();
            }
            self.$refs?.input?.$el?.querySelector('input').focus();
          }
        } else {
          this.$refs?.input?.$el?.querySelector('input').focus();
        }
      });
    },
    checkEnter() {
      return false;
    },
  },
};
</script>
<style lang="scss" scoped>
.submit-email {
  position: absolute;
  right: -7px;
  width: 60px;
  z-index: 9999999;
  top: 36px;
  height: 50px;
  border-radius: 0 4px 4px 0;
  opacity: 0;
}
</style>
